import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CustomerThemeEvidence } from './CustomerThemeEvidence';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraEmptyState, SpecraFormField, SpecraInput, SpecraOverlayDialog, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SharedCompanyPicker, SharedRecordPreview } from '../../components/SharedRecord';
import { useCompanyProfiles } from '../Delivery/Programs/referenceDataHooks';
import { SbaPursuitBadges } from '../Registries/records/company/SbaPursuitBadges';
import { useCompanySbaBadges } from '../Registries/records/company/sbaClient';
import { ASSERTION_STATUSES, assertionStatusWording, COMPETITION_ROLES, CONFIDENCE_LEVELS, EVIDENCE_BASES, EVIDENCE_CLAIM_TYPES, EVIDENCE_SOURCE_KINDS, EVIDENCE_STANCES, formatTs, INCLUSION_BASES, LIKELIHOOD_BANDS, labelOf } from './constants';
import { useCompetitiveField, useDeleteBlackHatInput, useDeleteCaptureCompetitionLink, useDeleteCaptureTeamingLink, useDeleteCompetitiveFieldParticipant, useRecordCompetitiveFieldAssessment, useRecordCompetitorEvidence, useReviewBlackHatInput, useUpsertBlackHatInput, useUpsertCaptureCompetitionLink, useUpsertCaptureTeamingLink, useUpsertCompetitiveFieldParticipant, useUpsertCompetitiveFieldStrategy } from './hooks';
import { ReadOnlyScope } from './record/ReadOnlyScope';
import { RowActionsMenu } from './record/RowActionsMenu';
import { TeamingAgreements } from './TeamingAgreements';
import { TeamingCandidates } from './TeamingCandidates';
function dateInputValue(ts) {
    if (!ts) return '';
    const d = ts.toDate();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
function parseDateInput(value) {
    if (!value) return null;
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
}
// Assertion status pill colors — hypothesis reads as caution, never as fact.
const ASSERTION_STYLE = {
    hypothesis: 'bg-amber-50 text-amber-800',
    corroborated: 'bg-indigo-50 text-indigo-700',
    established: 'bg-green-50 text-green-700'
};
// Evidence stance pill colors — contradicting evidence must be visibly
// distinguishable from supporting evidence.
const STANCE_STYLE = {
    supports: 'bg-green-50 text-green-700',
    contradicts: 'bg-red-50 text-red-700'
};
export function CaptureCompanyLinksPanel({ captureProjectId, teamingLinks, competitionLinks, correlationId }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "grid gap-4 lg:grid-cols-2",
        children: [
            /*#__PURE__*/ _jsx(TeamingList, {
                captureProjectId: captureProjectId,
                links: teamingLinks,
                correlationId: correlationId
            }),
            /*#__PURE__*/ _jsx(CompetitionList, {
                captureProjectId: captureProjectId,
                links: competitionLinks,
                correlationId: correlationId
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-3 lg:col-span-2",
                children: [
                    /*#__PURE__*/ _jsx(TeamingCandidates, {
                        captureProjectId: captureProjectId
                    }),
                    /*#__PURE__*/ _jsx(TeamingAgreements, {
                        captureProjectId: captureProjectId
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "lg:col-span-2",
                children: [
                    /*#__PURE__*/ _jsx(CustomerThemeEvidence, {
                        receiver: "competition",
                        objectId: captureProjectId
                    }),
                    /*#__PURE__*/ _jsx(CompetitiveFieldSection, {
                        captureProjectId: captureProjectId,
                        correlationId: correlationId
                    })
                ]
            })
        ]
    });
}
/** Also the pursuit Teaming section's link list (GRW-15), re-used rather than forked. */ export function TeamingList({ captureProjectId, links, correlationId, readOnly = false }) {
    const companies = useCompanyProfiles();
    const names = useCompanyNameMap(companies.data);
    const upsert = useUpsertCaptureTeamingLink(correlationId);
    const remove = useDeleteCaptureTeamingLink(correlationId);
    // DAT-25: one batched SBA badge read for the section.
    const sbaBadges = useCompanySbaBadges(links.map((l)=>l.companyProfileId));
    const [companyId, setCompanyId] = useState('');
    const [previewId, setPreviewId] = useState(null);
    const [workshare, setWorkshare] = useState('');
    const [addOpen, setAddOpen] = useState(false);
    const location = useLocation();
    const add = ()=>{
        if (!companyId) return;
        upsert.mutate({
            captureProjectId,
            companyProfileId: companyId,
            workshare
        }, {
            onSuccess: ()=>{
                setCompanyId('');
                setWorkshare('');
                setAddOpen(false);
            }
        });
    };
    return /*#__PURE__*/ _jsx(ReadOnlyScope, {
        readOnly: readOnly,
        children: /*#__PURE__*/ _jsxs(SpecraCard, {
            "data-testid": Testids.capture.teamingPanel,
            children: [
                /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                    className: "flex flex-row items-center justify-between space-y-0 py-4",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraCardTitle, {
                            children: "Teaming"
                        }),
                        !readOnly && links.length > 0 && /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            onClick: ()=>setAddOpen(true),
                            "data-testid": Testids.pursuitTeaming.addButton,
                            children: "Add teaming partner"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(SpecraCardContent, {
                    className: "space-y-3",
                    children: [
                        /*#__PURE__*/ _jsx(SharedRecordPreview, {
                            kind: "company",
                            id: previewId ?? '',
                            open: previewId !== null,
                            onClose: ()=>setPreviewId(null)
                        }),
                        !readOnly && /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
                            open: addOpen,
                            onOpenChange: setAddOpen,
                            title: "Add teaming partner",
                            size: "md",
                            testId: Testids.pursuitTeaming.dialog,
                            footerProps: {
                                onCancel: ()=>setAddOpen(false),
                                confirmLabel: 'Add',
                                onConfirm: add,
                                confirmProps: {
                                    disabled: !companyId || upsert.isPending,
                                    'data-testid': Testids.capture.teamingAddButton
                                }
                            },
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-3",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                children: "Company"
                                            }),
                                            /*#__PURE__*/ _jsx(SharedCompanyPicker, {
                                                value: companyId,
                                                onSelect: setCompanyId,
                                                onPreview: setPreviewId
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                children: "Workshare"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                value: workshare,
                                                onChange: (e)=>setWorkshare(e.target.value)
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(Link, {
                                        to: `/registries?tab=companies&returnTo=${encodeURIComponent(location.pathname + location.search)}`,
                                        className: "inline-block text-xs text-primary underline-offset-2 hover:underline",
                                        "data-testid": Testids.capture.teamingBrowseRegistriesLink,
                                        children: "Can't find a company? Browse the Registries"
                                    })
                                ]
                            })
                        }),
                        links.length === 0 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                            size: "compact",
                            title: "No teammates linked yet.",
                            description: "Link a registry company as a teaming partner.",
                            "data-testid": Testids.pursuitTeaming.emptyState,
                            action: readOnly ? undefined : /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                onClick: ()=>setAddOpen(true),
                                "data-testid": Testids.pursuitTeaming.addButton,
                                children: "Add teaming partner"
                            })
                        }) : /*#__PURE__*/ _jsx("ul", {
                            className: "space-y-2",
                            children: links.map((l)=>/*#__PURE__*/ _jsxs("li", {
                                    className: "flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm",
                                    "data-testid": Testids.capture.teamingRow,
                                    "data-link-id": l.id,
                                    children: [
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                names.get(l.companyProfileId) ?? l.companyProfileId,
                                                /*#__PURE__*/ _jsx(SbaPursuitBadges, {
                                                    badges: sbaBadges.data?.get(l.companyProfileId)
                                                }),
                                                l.workshare ? ` · ${l.workshare}` : '',
                                                l.nda ? ' · NDA' : '',
                                                l.ta ? ' · TA' : ''
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(RowActionsMenu, {
                                            rowName: names.get(l.companyProfileId) ?? 'this teammate',
                                            remove: {
                                                description: 'The company stays in the registry; only this teaming link is removed.',
                                                onConfirm: ()=>remove.mutate({
                                                        captureProjectId,
                                                        id: l.id
                                                    }),
                                                testId: Testids.capture.teamingRemoveButton,
                                                title: `Remove ${names.get(l.companyProfileId) ?? 'this teammate'}?`
                                            }
                                        })
                                    ]
                                }, l.id))
                        })
                    ]
                })
            ]
        })
    });
}
function CompetitionList({ captureProjectId, links, correlationId }) {
    const companies = useCompanyProfiles();
    const names = useCompanyNameMap(companies.data);
    const upsert = useUpsertCaptureCompetitionLink(correlationId);
    const remove = useDeleteCaptureCompetitionLink(correlationId);
    const [companyId, setCompanyId] = useState('');
    const [previewId, setPreviewId] = useState(null);
    const [role, setRole] = useState('incumbent');
    const add = ()=>{
        if (!companyId) return;
        upsert.mutate({
            captureProjectId,
            companyProfileId: companyId,
            role
        }, {
            onSuccess: ()=>setCompanyId('')
        });
    };
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        "data-testid": Testids.capture.competitionPanel,
        children: [
            /*#__PURE__*/ _jsx(SpecraCardHeader, {
                className: "py-4",
                children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                    children: "Competition"
                })
            }),
            /*#__PURE__*/ _jsxs(SpecraCardContent, {
                className: "space-y-3",
                children: [
                    /*#__PURE__*/ _jsx(SharedRecordPreview, {
                        kind: "company",
                        id: previewId ?? '',
                        open: previewId !== null,
                        onClose: ()=>setPreviewId(null)
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap items-end gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "min-w-[180px] flex-1 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        className: "text-xs font-medium",
                                        children: "Company"
                                    }),
                                    /*#__PURE__*/ _jsx(SharedCompanyPicker, {
                                        value: companyId,
                                        onSelect: setCompanyId,
                                        onPreview: setPreviewId
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "w-36 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        className: "text-xs font-medium",
                                        children: "Role"
                                    }),
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: role,
                                        onValueChange: setRole,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                "data-testid": Testids.capture.competitionRoleSelect,
                                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: COMPETITION_ROLES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: o.value,
                                                        children: o.label
                                                    }, o.value))
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                onClick: add,
                                disabled: !companyId || upsert.isPending,
                                "data-testid": Testids.capture.competitionAddButton,
                                iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                    className: "h-4 w-4"
                                }),
                                children: "Add"
                            })
                        ]
                    }),
                    links.length === 0 ? /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-muted-foreground",
                        children: "No competitors linked yet."
                    }) : /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-2",
                        children: links.map((l)=>/*#__PURE__*/ _jsxs("li", {
                                className: "flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm",
                                "data-testid": Testids.capture.competitionRow,
                                "data-link-id": l.id,
                                children: [
                                    /*#__PURE__*/ _jsxs("span", {
                                        children: [
                                            names.get(l.companyProfileId) ?? l.companyProfileId,
                                            ` · ${labelOf(COMPETITION_ROLES, l.role)}`
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(RowActionsMenu, {
                                        rowName: names.get(l.companyProfileId) ?? 'this competitor',
                                        remove: {
                                            description: 'The company stays in the registry; only this competitor link is removed.',
                                            onConfirm: ()=>remove.mutate({
                                                    captureProjectId,
                                                    id: l.id
                                                }),
                                            testId: Testids.capture.competitionRemoveButton,
                                            title: `Remove ${names.get(l.companyProfileId) ?? 'this competitor'}?`
                                        }
                                    })
                                ]
                            }, l.id))
                    })
                ]
            })
        ]
    });
}
// Evidence-backed competitive field (SAD-495): the participant list, its
// qualitative likelihood assessment + evidence, and its proposed strategy /
// Black Hat inputs are three distinct areas — never blended so a hypothesis
// cannot be mistaken for an established fact (B3.4-07.2, B3.4-14.3). No
// numeric price/rate field is ever requested here (B3.4-08).
//
// Also the pursuit Competitors section (GRW-15), which passes
// legacyInputs={false}: the per-participant Black Hat inputs are superseded by
// the Black hat section there and stay only on the flag-off tabbed page.
export function CompetitiveFieldSection({ captureProjectId, correlationId, legacyInputs = true, readOnly = false }) {
    const location = useLocation();
    const blackHatDestination = new URLSearchParams(location.search);
    blackHatDestination.set('tab', 'outputs');
    blackHatDestination.delete('outputOwner');
    const blackHatLink = legacyInputs ? {
        pathname: location.pathname,
        search: blackHatDestination.toString()
    } : {
        pathname: `/capture/${captureProjectId}/black-hat`
    };
    const companies = useCompanyProfiles();
    const names = useCompanyNameMap(companies.data);
    const field = useCompetitiveField(captureProjectId);
    const upsertParticipant = useUpsertCompetitiveFieldParticipant(correlationId);
    const removeParticipant = useDeleteCompetitiveFieldParticipant(correlationId);
    const [companyId, setCompanyId] = useState('');
    const [inclusionBasis, setInclusionBasis] = useState(INCLUSION_BASES[0].value);
    const [basisNote, setBasisNote] = useState('');
    const [addOpen, setAddOpen] = useState(false);
    const addParticipant = ()=>{
        if (!companyId) return;
        upsertParticipant.mutate({
            captureProjectId,
            companyProfileId: companyId,
            inclusionBasis,
            basisNote
        }, {
            onSuccess: ()=>{
                setCompanyId('');
                setBasisNote('');
                setAddOpen(false);
            }
        });
    };
    const entries = field.data?.entries ?? [];
    // DAT-25: one batched SBA badge read for the section.
    const sbaBadges = useCompanySbaBadges(entries.map((e)=>e.participant?.companyProfileId ?? ''));
    const blackHatInputs = field.data?.blackHatInputs ?? [];
    return /*#__PURE__*/ _jsx(ReadOnlyScope, {
        readOnly: readOnly,
        children: /*#__PURE__*/ _jsxs(SpecraCard, {
            "data-testid": Testids.capture.competitiveFieldPanel,
            children: [
                /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                    className: "flex flex-row items-center justify-between space-y-0 py-4",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraCardTitle, {
                            children: "Competitive field"
                        }),
                        !readOnly && entries.length > 0 && /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            onClick: ()=>setAddOpen(true),
                            "data-testid": Testids.pursuitCompetitors.addButton,
                            children: "Add competitor"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(SpecraCardContent, {
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-sm text-muted-foreground",
                            children: "Evidence-backed read on who else is chasing this — participation, likelihood, and strategy are recorded separately, and every likelihood starts as a hypothesis until evidence corroborates or establishes it."
                        }),
                        /*#__PURE__*/ _jsx(Link, {
                            "data-testid": Testids.blackHat.outputsLink,
                            to: blackHatLink,
                            children: legacyInputs ? 'Open qualitative Black Hat in Outputs' : 'Open Black hat'
                        }),
                        !readOnly && /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
                            open: addOpen,
                            onOpenChange: setAddOpen,
                            title: "Add competitor",
                            size: "md",
                            testId: Testids.pursuitCompetitors.dialog,
                            footerProps: {
                                onCancel: ()=>setAddOpen(false),
                                confirmLabel: 'Add participant',
                                onConfirm: addParticipant,
                                confirmProps: {
                                    disabled: !companyId || upsertParticipant.isPending,
                                    'data-testid': Testids.capture.competitiveFieldAddButton
                                }
                            },
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-3",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                children: "Company"
                                            }),
                                            /*#__PURE__*/ _jsxs(Select, {
                                                value: companyId || undefined,
                                                onValueChange: setCompanyId,
                                                children: [
                                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                                        "data-testid": Testids.capture.competitiveFieldCompanySelect,
                                                        children: /*#__PURE__*/ _jsx(SelectValue, {
                                                            placeholder: companies.isLoading ? 'Loading…' : 'Registry company…'
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsx(SelectContent, {
                                                        children: (companies.data ?? []).map((c)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                                value: c.id,
                                                                children: c.name
                                                            }, c.id))
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                children: "Basis of inclusion"
                                            }),
                                            /*#__PURE__*/ _jsxs(Select, {
                                                value: inclusionBasis,
                                                onValueChange: setInclusionBasis,
                                                children: [
                                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                                        "data-testid": Testids.capture.competitiveFieldInclusionBasisSelect,
                                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                                    }),
                                                    /*#__PURE__*/ _jsx(SelectContent, {
                                                        children: INCLUSION_BASES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                                value: o.value,
                                                                children: o.label
                                                            }, o.value))
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                children: "Basis note"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                value: basisNote,
                                                onChange: (e)=>setBasisNote(e.target.value),
                                                placeholder: "Why this company is in the field",
                                                "data-testid": Testids.capture.competitiveFieldBasisNoteInput
                                            })
                                        ]
                                    })
                                ]
                            })
                        }),
                        entries.length === 0 ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                            size: "compact",
                            title: "No competitive field participants yet.",
                            description: "Add the companies you expect to compete against.",
                            "data-testid": Testids.pursuitCompetitors.emptyState,
                            action: readOnly ? undefined : /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                onClick: ()=>setAddOpen(true),
                                "data-testid": Testids.pursuitCompetitors.addButton,
                                children: "Add competitor"
                            })
                        }) : /*#__PURE__*/ _jsx("ul", {
                            className: "space-y-3",
                            children: entries.map((entry)=>/*#__PURE__*/ _jsx(CompetitiveFieldParticipantRow, {
                                    captureProjectId: captureProjectId,
                                    correlationId: correlationId,
                                    entry: entry,
                                    companyName: names.get(entry.participant?.companyProfileId ?? '') ?? entry.participant?.companyProfileId ?? '',
                                    sbaBadges: sbaBadges.data?.get(entry.participant?.companyProfileId ?? ''),
                                    blackHatInputs: blackHatInputs.filter((b)=>b.participantId === entry.participant?.id),
                                    legacyInputs: legacyInputs,
                                    readOnly: readOnly,
                                    onRemove: ()=>entry.participant && removeParticipant.mutate({
                                            captureProjectId,
                                            id: entry.participant.id
                                        })
                                }, entry.participant?.id))
                        })
                    ]
                })
            ]
        })
    });
}
function CompetitiveFieldParticipantRow({ captureProjectId, correlationId, entry, companyName, blackHatInputs, sbaBadges, legacyInputs, readOnly = false, onRemove }) {
    const participant = entry.participant;
    const assessment = entry.currentAssessment;
    const strategy = entry.strategy;
    const recordAssessment = useRecordCompetitiveFieldAssessment(correlationId);
    const recordEvidence = useRecordCompetitorEvidence(correlationId);
    const upsertStrategy = useUpsertCompetitiveFieldStrategy(correlationId);
    const upsertBlackHat = useUpsertBlackHatInput(correlationId);
    const removeBlackHat = useDeleteBlackHatInput(correlationId);
    const reviewBlackHat = useReviewBlackHatInput(correlationId);
    const [likelihoodBand, setLikelihoodBand] = useState(assessment?.likelihoodBand || LIKELIHOOD_BANDS[0].value);
    const [assertionStatus, setAssertionStatus] = useState(assessment?.assertionStatus || ASSERTION_STATUSES[0].value);
    const [confidence, setConfidence] = useState(assessment?.confidence || CONFIDENCE_LEVELS[0].value);
    const [rationale, setRationale] = useState('');
    const [asOfDate, setAsOfDate] = useState(dateInputValue(assessment?.asOfDate));
    const [sourceKind, setSourceKind] = useState(EVIDENCE_SOURCE_KINDS[0].value);
    const [sourceRef, setSourceRef] = useState('');
    const [sourceDate, setSourceDate] = useState('');
    const [claimType, setClaimType] = useState(EVIDENCE_CLAIM_TYPES[0].value);
    const [stance, setStance] = useState(EVIDENCE_STANCES[0].value);
    const [evidenceConfidence, setEvidenceConfidence] = useState(CONFIDENCE_LEVELS[0].value);
    const [evidenceRationale, setEvidenceRationale] = useState('');
    const [inferenceAssumptions, setInferenceAssumptions] = useState('');
    const [basis, setBasis] = useState(EVIDENCE_BASES[0].value);
    const [positioning, setPositioning] = useState(strategy?.positioning ?? '');
    const [discriminators, setDiscriminators] = useState(strategy?.discriminators ?? '');
    const [response, setResponse] = useState(strategy?.response ?? '');
    const [advantages, setAdvantages] = useState('');
    const [vulnerabilities, setVulnerabilities] = useState('');
    const [likelyApproaches, setLikelyApproaches] = useState('');
    const [ourResponse, setOurResponse] = useState('');
    const [openQuestions, setOpenQuestions] = useState('');
    // Id of the current input being revised; saving then writes a new revision.
    const [reviseId, setReviseId] = useState('');
    const [reviewNote, setReviewNote] = useState('');
    const currentBlackHats = blackHatInputs.filter((b)=>!b.supersededAt);
    const blackHatHistory = blackHatInputs.filter((b)=>b.supersededAt);
    if (!participant) return null;
    const submitAssessment = ()=>{
        recordAssessment.mutate({
            captureProjectId,
            participantId: participant.id,
            likelihoodBand,
            assertionStatus,
            confidence,
            rationale,
            asOfDate: parseDateInput(asOfDate)
        });
    };
    const submitEvidence = ()=>{
        recordEvidence.mutate({
            captureProjectId,
            participantId: participant.id,
            sourceKind,
            sourceRef,
            sourceDate: parseDateInput(sourceDate),
            claimType,
            stance,
            confidence: evidenceConfidence,
            rationale: evidenceRationale,
            inferenceAssumptions,
            basis
        }, {
            onSuccess: ()=>{
                setSourceRef('');
                setSourceDate('');
                setEvidenceRationale('');
                setInferenceAssumptions('');
            }
        });
    };
    const submitStrategy = ()=>{
        upsertStrategy.mutate({
            captureProjectId,
            id: strategy?.id,
            participantId: participant.id,
            positioning,
            discriminators,
            response
        });
    };
    const submitBlackHat = ()=>{
        upsertBlackHat.mutate({
            captureProjectId,
            id: reviseId || undefined,
            participantId: participant.id,
            competitorAdvantages: advantages,
            competitorVulnerabilities: vulnerabilities,
            likelyApproaches,
            ourResponse,
            openQuestions
        }, {
            onSuccess: ()=>{
                setAdvantages('');
                setVulnerabilities('');
                setLikelyApproaches('');
                setOurResponse('');
                setOpenQuestions('');
                setReviseId('');
            }
        });
    };
    return /*#__PURE__*/ _jsxs("li", {
        className: "space-y-3 rounded-md border p-3 text-sm",
        "data-testid": Testids.capture.competitiveParticipantRow,
        "data-participant-id": participant.id,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-start justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-medium",
                                children: companyName
                            }),
                            /*#__PURE__*/ _jsx(SbaPursuitBadges, {
                                badges: sbaBadges
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: "text-xs text-muted-foreground",
                                children: [
                                    ' · ',
                                    labelOf(INCLUSION_BASES, participant.inclusionBasis)
                                ]
                            }),
                            participant.basisNote && /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-muted-foreground",
                                children: participant.basisNote
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(RowActionsMenu, {
                        rowName: companyName,
                        readOnly: readOnly,
                        remove: {
                            description: 'Its likelihood, strategy and evidence stop counting for this pursuit.',
                            onConfirm: onRemove,
                            testId: Testids.capture.competitiveParticipantRemoveButton,
                            title: `Remove ${companyName} from the competitive field?`
                        }
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2 rounded-md border p-2",
                "data-testid": Testids.capture.competitiveLikelihoodArea,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: `inline-flex items-center rounded-full px-2 py-0.5 text-xs ${ASSERTION_STYLE[assessment?.assertionStatus ?? 'hypothesis'] ?? ASSERTION_STYLE.hypothesis}`,
                                "data-testid": Testids.capture.competitiveAssertionBadge,
                                children: assertionStatusWording(assessment?.assertionStatus ?? 'hypothesis')
                            }),
                            assessment && /*#__PURE__*/ _jsxs("span", {
                                className: "text-xs text-muted-foreground",
                                children: [
                                    labelOf(LIKELIHOOD_BANDS, assessment.likelihoodBand),
                                    " likelihood ·",
                                    ' ',
                                    labelOf(CONFIDENCE_LEVELS, assessment.confidence),
                                    " confidence · as of",
                                    ' ',
                                    formatTs(assessment.asOfDate)
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap items-end gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "w-36 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        className: "text-xs font-medium",
                                        children: "Likelihood"
                                    }),
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: likelihoodBand,
                                        onValueChange: setLikelihoodBand,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                "data-testid": Testids.capture.competitiveLikelihoodBandSelect,
                                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: LIKELIHOOD_BANDS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: o.value,
                                                        children: o.label
                                                    }, o.value))
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "w-36 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        className: "text-xs font-medium",
                                        children: "Assertion status"
                                    }),
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: assertionStatus,
                                        onValueChange: setAssertionStatus,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                "data-testid": Testids.capture.competitiveAssertionStatusSelect,
                                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: ASSERTION_STATUSES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: o.value,
                                                        children: o.label
                                                    }, o.value))
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "w-32 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        className: "text-xs font-medium",
                                        children: "Confidence"
                                    }),
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: confidence,
                                        onValueChange: setConfidence,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                "data-testid": Testids.capture.competitiveConfidenceSelect,
                                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: CONFIDENCE_LEVELS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: o.value,
                                                        children: o.label
                                                    }, o.value))
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "w-36 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        className: "text-xs font-medium",
                                        children: "As of"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        type: "date",
                                        value: asOfDate,
                                        onChange: (e)=>setAsOfDate(e.target.value),
                                        "data-testid": Testids.capture.competitiveAssessmentDateInput
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "min-w-[180px] flex-1 space-y-1",
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        className: "text-xs font-medium",
                                        children: "Rationale"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        value: rationale,
                                        onChange: (e)=>setRationale(e.target.value),
                                        placeholder: "Why this band and status",
                                        "data-testid": Testids.capture.competitiveAssessmentRationaleInput
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                onClick: submitAssessment,
                                disabled: recordAssessment.isPending,
                                "data-testid": Testids.capture.competitiveAssessButton,
                                children: assessment ? 'Revise' : 'Assess'
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-1",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-xs font-medium",
                                children: "Evidence"
                            }),
                            entry.evidence.length === 0 ? /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-muted-foreground",
                                children: "No evidence recorded."
                            }) : /*#__PURE__*/ _jsx("ul", {
                                className: "space-y-1",
                                children: entry.evidence.map((e)=>/*#__PURE__*/ _jsxs("li", {
                                        className: "rounded border px-2 py-1 text-xs",
                                        "data-testid": Testids.capture.competitiveEvidenceRow,
                                        "data-stance": e.stance,
                                        "data-basis": e.basis,
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: `mr-1 inline-flex items-center rounded-full px-1.5 py-0.5 ${STANCE_STYLE[e.stance] ?? STANCE_STYLE.supports}`,
                                                children: labelOf(EVIDENCE_STANCES, e.stance)
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraBadge, {
                                                className: "mr-1",
                                                variant: e.basis === 'assumption' ? 'warning' : 'neutral',
                                                "data-testid": Testids.capture.competitiveEvidenceBasisBadge,
                                                "data-basis": e.basis,
                                                children: e.basis === 'assumption' ? 'Assumption — not verified' : 'Sourced'
                                            }),
                                            labelOf(EVIDENCE_CLAIM_TYPES, e.claimType),
                                            " ·",
                                            ' ',
                                            labelOf(EVIDENCE_SOURCE_KINDS, e.sourceKind),
                                            e.sourceRef ? ` (${e.sourceRef})` : '',
                                            " · ",
                                            formatTs(e.sourceDate),
                                            " ·",
                                            ' ',
                                            labelOf(CONFIDENCE_LEVELS, e.confidence),
                                            " confidence",
                                            e.rationale ? ` — ${e.rationale}` : ''
                                        ]
                                    }, e.id))
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap items-end gap-2",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "w-40 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                children: "Source"
                                            }),
                                            /*#__PURE__*/ _jsxs(Select, {
                                                value: sourceKind,
                                                onValueChange: setSourceKind,
                                                children: [
                                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                                        "data-testid": Testids.capture.competitiveEvidenceSourceKindSelect,
                                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                                    }),
                                                    /*#__PURE__*/ _jsx(SelectContent, {
                                                        children: EVIDENCE_SOURCE_KINDS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                                value: o.value,
                                                                children: o.label
                                                            }, o.value))
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "min-w-[140px] flex-1 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                children: "Reference"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                value: sourceRef,
                                                onChange: (e)=>setSourceRef(e.target.value),
                                                placeholder: "Link or citation",
                                                "data-testid": Testids.capture.competitiveEvidenceSourceRefInput
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "w-36 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                children: "Date"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                type: "date",
                                                value: sourceDate,
                                                onChange: (e)=>setSourceDate(e.target.value),
                                                "data-testid": Testids.capture.competitiveEvidenceDateInput
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraFormField, {
                                        className: "w-40",
                                        label: "Basis",
                                        children: /*#__PURE__*/ _jsxs(Select, {
                                            value: basis,
                                            onValueChange: setBasis,
                                            children: [
                                                /*#__PURE__*/ _jsx(SelectTrigger, {
                                                    "data-testid": Testids.capture.competitiveEvidenceBasisSelect,
                                                    children: /*#__PURE__*/ _jsx(SelectValue, {})
                                                }),
                                                /*#__PURE__*/ _jsx(SelectContent, {
                                                    children: EVIDENCE_BASES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                            value: o.value,
                                                            children: o.label
                                                        }, o.value))
                                                })
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "w-44 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                children: "Claim type"
                                            }),
                                            /*#__PURE__*/ _jsxs(Select, {
                                                value: claimType,
                                                onValueChange: setClaimType,
                                                children: [
                                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                                        "data-testid": Testids.capture.competitiveEvidenceClaimTypeSelect,
                                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                                    }),
                                                    /*#__PURE__*/ _jsx(SelectContent, {
                                                        children: EVIDENCE_CLAIM_TYPES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                                value: o.value,
                                                                children: o.label
                                                            }, o.value))
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "w-32 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                children: "Stance"
                                            }),
                                            /*#__PURE__*/ _jsxs(Select, {
                                                value: stance,
                                                onValueChange: setStance,
                                                children: [
                                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                                        "data-testid": Testids.capture.competitiveEvidenceStanceSelect,
                                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                                    }),
                                                    /*#__PURE__*/ _jsx(SelectContent, {
                                                        children: EVIDENCE_STANCES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                                value: o.value,
                                                                children: o.label
                                                            }, o.value))
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "w-32 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                children: "Confidence"
                                            }),
                                            /*#__PURE__*/ _jsxs(Select, {
                                                value: evidenceConfidence,
                                                onValueChange: setEvidenceConfidence,
                                                children: [
                                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                                        "data-testid": Testids.capture.competitiveEvidenceConfidenceSelect,
                                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                                    }),
                                                    /*#__PURE__*/ _jsx(SelectContent, {
                                                        children: CONFIDENCE_LEVELS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                                value: o.value,
                                                                children: o.label
                                                            }, o.value))
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "min-w-[160px] flex-1 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                children: "Rationale"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                value: evidenceRationale,
                                                onChange: (e)=>setEvidenceRationale(e.target.value),
                                                placeholder: "What this evidence shows",
                                                "data-testid": Testids.capture.competitiveEvidenceRationaleInput
                                            })
                                        ]
                                    }),
                                    claimType === 'rate_or_price_inference' && /*#__PURE__*/ _jsxs("div", {
                                        className: "min-w-[160px] flex-1 space-y-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("label", {
                                                className: "text-xs font-medium",
                                                children: "Inference & assumptions"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                value: inferenceAssumptions,
                                                onChange: (e)=>setInferenceAssumptions(e.target.value),
                                                placeholder: "Labeled inference and the assumptions behind it",
                                                "data-testid": Testids.capture.competitiveEvidenceInferenceAssumptionsInput
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        onClick: submitEvidence,
                                        disabled: recordEvidence.isPending,
                                        "data-testid": Testids.capture.competitiveEvidenceAddButton,
                                        iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                            className: "h-3.5 w-3.5"
                                        }),
                                        children: "Add evidence"
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2 rounded-md border p-2",
                "data-testid": Testids.capture.competitiveStrategyArea,
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-xs font-medium",
                        children: "Proposed strategy"
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: positioning,
                        onChange: (e)=>setPositioning(e.target.value),
                        placeholder: "Positioning",
                        rows: 2,
                        "data-testid": Testids.capture.competitiveStrategyPositioningInput
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: discriminators,
                        onChange: (e)=>setDiscriminators(e.target.value),
                        placeholder: "Discriminators",
                        rows: 2,
                        "data-testid": Testids.capture.competitiveStrategyDiscriminatorsInput
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: response,
                        onChange: (e)=>setResponse(e.target.value),
                        placeholder: "Response",
                        rows: 2,
                        "data-testid": Testids.capture.competitiveStrategyResponseInput
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex justify-end",
                        children: /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            onClick: submitStrategy,
                            disabled: upsertStrategy.isPending,
                            "data-testid": Testids.capture.competitiveStrategySaveButton,
                            children: "Save strategy"
                        })
                    })
                ]
            }),
            legacyInputs && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2 rounded-md border p-2",
                "data-testid": Testids.capture.competitiveBlackHatArea,
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-xs font-medium",
                        children: "Black Hat"
                    }),
                    currentBlackHats.length === 0 ? /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-muted-foreground",
                        children: "No Black Hat inputs yet."
                    }) : /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-2",
                        children: currentBlackHats.map((b)=>/*#__PURE__*/ _jsxs("li", {
                                className: "space-y-1 rounded border p-2 text-xs",
                                "data-testid": Testids.capture.competitiveBlackHatRow,
                                children: [
                                    /*#__PURE__*/ _jsxs("p", {
                                        className: "flex flex-wrap items-center gap-2 text-muted-foreground",
                                        "data-testid": Testids.capture.competitiveBlackHatAuthor,
                                        "data-actor-user-id": b.actorUserId,
                                        children: [
                                            /*#__PURE__*/ _jsxs("span", {
                                                children: [
                                                    b.supersedesId ? 'Revised' : 'Recorded',
                                                    " by ",
                                                    b.actorUserId || 'unknown',
                                                    " ·",
                                                    ' ',
                                                    formatTs(b.createdAt)
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraBadge, {
                                                variant: b.reviewedAt ? 'success' : 'warning',
                                                "data-testid": Testids.capture.competitiveBlackHatReviewBadge,
                                                "data-review": b.reviewedAt ? 'reviewed' : 'unreviewed',
                                                children: b.reviewedAt ? `Reviewed by ${b.reviewedBy} · ${formatTs(b.reviewedAt)}` : 'Not yet reviewed'
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "font-medium",
                                                children: "Advantages:"
                                            }),
                                            " ",
                                            b.competitorAdvantages || '—'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "font-medium",
                                                children: "Vulnerabilities:"
                                            }),
                                            ' ',
                                            b.competitorVulnerabilities || '—'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "font-medium",
                                                children: "Likely approaches:"
                                            }),
                                            ' ',
                                            b.likelyApproaches || '—'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "font-medium",
                                                children: "Our response:"
                                            }),
                                            " ",
                                            b.ourResponse || '—'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "font-medium",
                                                children: "Open questions:"
                                            }),
                                            " ",
                                            b.openQuestions || '—'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-wrap items-center justify-end gap-2",
                                        children: [
                                            !b.reviewedAt && /*#__PURE__*/ _jsxs(_Fragment, {
                                                children: [
                                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                                        className: "h-8 max-w-56",
                                                        value: reviewNote,
                                                        onChange: (e)=>setReviewNote(e.target.value),
                                                        placeholder: "Review note (optional)",
                                                        "data-testid": Testids.capture.competitiveBlackHatReviewNoteInput
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                                        size: "sm",
                                                        disabled: reviewBlackHat.isPending,
                                                        "data-testid": Testids.capture.competitiveBlackHatReviewButton,
                                                        onClick: ()=>reviewBlackHat.mutate({
                                                                captureProjectId,
                                                                id: b.id,
                                                                reviewNote
                                                            }, {
                                                                onSuccess: ()=>setReviewNote('')
                                                            }),
                                                        children: "Mark reviewed"
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(RowActionsMenu, {
                                                rowName: "this Black Hat input",
                                                readOnly: readOnly,
                                                onEdit: {
                                                    label: 'Revise',
                                                    testId: Testids.capture.competitiveBlackHatReviseButton,
                                                    onSelect: ()=>{
                                                        setReviseId(b.id);
                                                        setAdvantages(b.competitorAdvantages);
                                                        setVulnerabilities(b.competitorVulnerabilities);
                                                        setLikelyApproaches(b.likelyApproaches);
                                                        setOurResponse(b.ourResponse);
                                                        setOpenQuestions(b.openQuestions);
                                                    }
                                                },
                                                remove: {
                                                    immediate: true,
                                                    testId: Testids.capture.competitiveBlackHatRemoveButton,
                                                    onConfirm: ()=>removeBlackHat.mutate({
                                                            captureProjectId,
                                                            id: b.id
                                                        }),
                                                    title: '',
                                                    description: ''
                                                }
                                            })
                                        ]
                                    })
                                ]
                            }, b.id))
                    }),
                    blackHatHistory.length > 0 && /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-1",
                        children: blackHatHistory.map((b)=>/*#__PURE__*/ _jsxs("li", {
                                className: "rounded border border-dashed px-2 py-1 text-xs text-muted-foreground",
                                "data-testid": Testids.capture.competitiveBlackHatHistoryRow,
                                "data-actor-user-id": b.actorUserId,
                                children: [
                                    "Superseded ",
                                    formatTs(b.supersededAt),
                                    " · by ",
                                    b.actorUserId || 'unknown',
                                    " ·",
                                    ' ',
                                    b.reviewedAt ? `reviewed by ${b.reviewedBy}` : 'never reviewed',
                                    " —",
                                    ' ',
                                    b.competitorAdvantages || b.likelyApproaches || b.ourResponse || '—'
                                ]
                            }, b.id))
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: advantages,
                        onChange: (e)=>setAdvantages(e.target.value),
                        placeholder: "Competitor advantages",
                        rows: 2,
                        "data-testid": Testids.capture.competitiveBlackHatAdvantagesInput
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: vulnerabilities,
                        onChange: (e)=>setVulnerabilities(e.target.value),
                        placeholder: "Competitor vulnerabilities",
                        rows: 2,
                        "data-testid": Testids.capture.competitiveBlackHatVulnerabilitiesInput
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: likelyApproaches,
                        onChange: (e)=>setLikelyApproaches(e.target.value),
                        placeholder: "Likely approaches",
                        rows: 2,
                        "data-testid": Testids.capture.competitiveBlackHatApproachesInput
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: ourResponse,
                        onChange: (e)=>setOurResponse(e.target.value),
                        placeholder: "Our response",
                        rows: 2,
                        "data-testid": Testids.capture.competitiveBlackHatResponseInput
                    }),
                    /*#__PURE__*/ _jsx(SpecraTextarea, {
                        value: openQuestions,
                        onChange: (e)=>setOpenQuestions(e.target.value),
                        placeholder: "Open questions",
                        rows: 2,
                        "data-testid": Testids.capture.competitiveBlackHatQuestionsInput
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex justify-end",
                        children: /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            onClick: submitBlackHat,
                            disabled: upsertBlackHat.isPending,
                            "data-testid": Testids.capture.competitiveBlackHatAddButton,
                            iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                className: "h-3.5 w-3.5"
                            }),
                            children: reviseId ? 'Save revision' : 'Add Black Hat input'
                        })
                    })
                ]
            })
        ]
    });
}
function useCompanyNameMap(profiles) {
    return useMemo(()=>{
        const m = new Map();
        for (const p of profiles ?? []){
            m.set(p.id, p.name);
        }
        return m;
    }, [
        profiles
    ]);
}
